import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import {
  TrendingUp, Award, AlertTriangle, Sparkles, Target, Star,
  BookOpen, CheckCircle2, Calendar,
} from "lucide-react";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { useChildCtx } from "./ChildDashboard";

const NAVY = "#1B2C5A";
const LIME = "#9DCD3F";
const AMBER = "#F59E0B";
const EMERALD = "#10B981";
const RED = "#DC2626";

/**
 * ChildInsights — analytical view of the child's strengths, weak subjects,
 * attendance trends, achievement badges and personalised recommendations.
 * Everything derived from the report card + attendance data already fetched.
 */
const ChildInsights = () => {
  const { reportCard, attendance, rcStatus, child } = useChildCtx();

  const stats = useMemo(() => {
    if (!reportCard?.subjects?.length) return null;
    const subs = reportCard.subjects;
    const sorted = [...subs].sort((a, b) => b.total - a.total);
    const strong = sorted.slice(0, Math.min(3, sorted.length));
    const weak = sorted.slice(-Math.min(3, sorted.length)).reverse().filter((s) => s.total < 70);
    const above = subs.filter((s) => (s.total || 0) > (s.class_average || 0)).length;
    const below = subs.length - above;
    return { strong, weak, above, below, sorted };
  }, [reportCard]);

  const attPct = attendance ? Math.round(100 * (attendance.counts.present + attendance.counts.late) / Math.max(1, attendance.total)) : null;

  // Achievement badges — derived from data
  const badges = useMemo(() => {
    const b = [];
    if (reportCard?.overall?.average_pct >= 75) b.push({ icon: <Award />, label: "Excellent Scholar", tone: "gold", desc: "Overall average above 75%" });
    if (reportCard?.overall?.position === 1) b.push({ icon: <Star />, label: "Top of Class", tone: "gold", desc: "1st position in class" });
    if (reportCard?.overall?.position && reportCard.overall.position <= 3) b.push({ icon: <Sparkles />, label: "Top 3", tone: "silver", desc: "In the top 3 of the class" });
    if (attPct != null && attPct >= 95) b.push({ icon: <Calendar />, label: "Perfect Presence", tone: "gold", desc: "≥95% attendance" });
    else if (attPct != null && attPct >= 85) b.push({ icon: <CheckCircle2 />, label: "Reliable", tone: "silver", desc: "≥85% attendance" });
    if (stats && stats.strong[0]?.total >= 80) b.push({ icon: <BookOpen />, label: `${stats.strong[0].subject_name} Star`, tone: "brand", desc: `Highest score: ${stats.strong[0].total}` });
    if (stats && stats.above > stats.below) b.push({ icon: <TrendingUp />, label: "Above the Curve", tone: "brand", desc: `${stats.above}/${stats.above + stats.below} subjects above class average` });
    return b;
  }, [reportCard, attendance, stats, attPct]);

  const recommendations = useMemo(() => {
    const recs = [];
    if (!stats) return recs;
    // subject-based
    stats.weak.forEach((s) => {
      recs.push({
        icon: <Target />,
        title: `Boost ${s.subject_name}`,
        text: `Current score ${s.total} · Class average ${s.class_average}. Set aside 30 minutes daily for practice and consider extra tuition if possible.`,
      });
    });
    if (attPct != null && attPct < 75) {
      recs.push({
        icon: <Calendar />,
        title: "Improve Attendance",
        text: `${child.first_name || "The student"} attended ${attPct}% of school days. Attendance below 75% loses the automatic 10 marks in every subject.`,
      });
    }
    if (reportCard?.overall?.average_pct < 40) {
      recs.push({
        icon: <AlertTriangle />,
        title: "Pass Mark Attention",
        text: "Overall average is below the pass mark (40%). Please arrange a meeting with the class teacher.",
      });
    }
    if (stats.strong[0] && stats.strong[0].total >= 80) {
      recs.push({
        icon: <Sparkles />,
        title: `Nurture Talent — ${stats.strong[0].subject_name}`,
        text: `Consistent excellence in ${stats.strong[0].subject_name}. Consider competitions, clubs or advanced reading.`,
      });
    }
    if (recs.length === 0) {
      recs.push({
        icon: <CheckCircle2 />,
        title: "Keep Up the Great Work",
        text: `${child.first_name || "The student"} is performing well across all subjects. Maintain the routine and keep encouraging them.`,
      });
    }
    return recs;
  }, [stats, reportCard, attPct, child]);

  const strengthsData = useMemo(() => {
    if (!reportCard?.subjects?.length) return [];
    return reportCard.subjects.map((s) => ({
      subject: (s.subject_name || "").slice(0, 8),
      score: s.total,
      class: s.class_average || 0,
    }));
  }, [reportCard]);

  if (rcStatus === "draft") {
    return (
      <Card className="p-10 text-center border-amber-200 bg-amber-50/40" data-testid="insights-draft">
        <TrendingUp className="w-10 h-10 text-amber-600 mx-auto mb-3" />
        <h3 className="font-display text-xl text-amber-900">Insights available after results are published</h3>
        <p className="text-sm text-amber-800 mt-2 max-w-md mx-auto">Personalised strengths, weak-subject analysis and recommendations appear here once the term&apos;s report card is published by the school.</p>
      </Card>
    );
  }

  if (!reportCard?.subjects?.length) {
    return <div className="text-center py-16 text-sm text-[#475569] italic">Not enough graded data to compute insights yet.</div>;
  }

  return (
    <div className="space-y-4" data-testid="tab-insights">
      {/* header line */}
      <div className="rounded-2xl bg-gradient-to-r from-[#1B2C5A] to-[#243671] text-white p-5 flex items-start gap-4 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-56 h-56 bg-[#9DCD3F]/20 rounded-full blur-3xl" />
        <div className="w-12 h-12 rounded-2xl bg-white/15 grid place-items-center shrink-0 relative z-10"><TrendingUp className="w-6 h-6 text-[#9DCD3F]" /></div>
        <div className="relative z-10">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#9DCD3F]">Growth Insights</div>
          <h2 className="font-display text-2xl mt-1">{child.first_name}&apos;s Progress Report</h2>
          <p className="text-sm text-white/70 mt-1">Analytics powered by CA1, CA2, exam and attendance data across all subjects this term.</p>
        </div>
      </div>

      {/* strengths + weak side by side */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 border-[#E2E8F0] lg:col-span-2">
          <div className="flex items-center gap-2 mb-2"><span className="text-[#9DCD3F]"><Award className="w-4 h-4" /></span><h3 className="font-display text-base text-[#1B2C5A]">Subject Landscape</h3></div>
          <p className="text-xs text-[#475569] mb-2">Radar chart shows how your child performs across every subject relative to the class average.</p>
          <div className="h-72">
            <ResponsiveContainer>
              <RadarChart data={strengthsData}>
                <PolarGrid stroke="#E2E8F0" />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: "#475569" }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: "#475569" }} />
                <Radar name={child.first_name || "Score"} dataKey="score" stroke={LIME} fill={LIME} fillOpacity={0.4} strokeWidth={2} />
                <Radar name="Class Avg" dataKey="class" stroke={NAVY} fill={NAVY} fillOpacity={0.15} strokeWidth={2} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-[#E2E8F0]">
          <div className="flex items-center gap-2 mb-3"><span className="text-[#9DCD3F]"><Target className="w-4 h-4" /></span><h3 className="font-display text-base text-[#1B2C5A]">Strengths & Weak Areas</h3></div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium mb-2">Strongest Subjects</div>
            <div className="space-y-1.5">
              {stats.strong.map((s) => (
                <div key={s.subject_id} className="flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-[#1B2C5A] flex-1 truncate">{s.subject_name}</span>
                  <span className="font-semibold text-emerald-700">{s.total}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider text-red-700 font-medium mb-2">Needs Attention</div>
            {stats.weak.length === 0 ? (
              <p className="text-xs text-[#475569] italic">No weak subjects &mdash; every subject is above 70%.</p>
            ) : (
              <div className="space-y-1.5">
                {stats.weak.map((s) => (
                  <div key={s.subject_id} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    <span className="text-[#1B2C5A] flex-1 truncate">{s.subject_name}</span>
                    <span className="font-semibold text-red-700">{s.total}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* class comparison */}
      <Card className="p-5 border-[#E2E8F0]">
        <div className="flex items-center gap-2 mb-3"><span className="text-[#9DCD3F]"><BookOpen className="w-4 h-4" /></span><h3 className="font-display text-base text-[#1B2C5A]">You vs. Class Average, per Subject</h3></div>
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={reportCard.subjects.map((s) => ({ subject: (s.subject_name || "").slice(0, 10), diff: (s.total || 0) - (s.class_average || 0) }))}>
              <CartesianGrid vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="subject" tick={{ fontSize: 10, fill: "#475569" }} interval={0} angle={-25} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10, fill: "#475569" }} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => [(v > 0 ? "+" : "") + v + " pts above class avg", "Difference"]} />
              <Bar dataKey="diff" radius={[6, 6, 0, 0]}>
                {reportCard.subjects.map((s, i) => (<Cell key={i} fill={((s.total || 0) - (s.class_average || 0)) >= 0 ? EMERALD : RED} />))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Badges */}
      {badges.length > 0 && (
        <Card className="p-5 border-[#E2E8F0]">
          <div className="flex items-center gap-2 mb-3"><span className="text-[#9DCD3F]"><Star className="w-4 h-4" /></span><h3 className="font-display text-base text-[#1B2C5A]">Achievements ({badges.length})</h3></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {badges.map((b, i) => (
              <div key={i} className={
                "rounded-xl p-3 border flex items-center gap-3 transition hover:shadow-md " + (
                  b.tone === "gold" ? "border-amber-200 bg-amber-50/60" :
                  b.tone === "silver" ? "border-slate-200 bg-slate-50/60" :
                  "border-[#9DCD3F]/40 bg-[#9DCD3F]/8"
                )
              } data-testid={`badge-${i}`}>
                <div className={
                  "w-11 h-11 rounded-full grid place-items-center shrink-0 " + (
                    b.tone === "gold" ? "bg-amber-100 text-amber-700" :
                    b.tone === "silver" ? "bg-slate-100 text-slate-700" :
                    "bg-[#9DCD3F]/20 text-[#1B2C5A]"
                  )
                }>{b.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#1B2C5A]">{b.label}</div>
                  <div className="text-[11px] text-[#475569] mt-0.5">{b.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recommendations */}
      <Card className="p-5 border-[#E2E8F0]">
        <div className="flex items-center gap-2 mb-3"><span className="text-[#9DCD3F]"><Sparkles className="w-4 h-4" /></span><h3 className="font-display text-base text-[#1B2C5A]">Personalised Recommendations</h3></div>
        <div className="space-y-2">
          {recommendations.map((r, i) => (
            <div key={i} className="rounded-lg border border-[#9DCD3F]/30 bg-[#9DCD3F]/5 p-3 flex gap-3 items-start" data-testid={`rec-${i}`}>
              <span className="w-9 h-9 rounded-lg bg-white border border-[#9DCD3F]/40 grid place-items-center text-[#9DCD3F] shrink-0">{r.icon}</span>
              <div>
                <div className="text-sm font-medium text-[#1B2C5A]">{r.title}</div>
                <div className="text-xs text-[#475569] mt-0.5">{r.text}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default ChildInsights;
